import { create } from 'zustand';
import { apiFetch } from '@/lib/api';
import { storageKey } from '@/config/app';
import { setLocale, getLocaleOverride } from '@/lib/i18n';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  status?: string;
  plan_key?: string;
  created_at?: string;
  trial_ends_at?: string;
  onboarding_completed?: boolean;
  onboarding_completed_at?: string | null;
  default_language?: string;
  address_street?: string | null;
  address_city?: string | null;
  address_zip?: string | null;
  address_country?: string | null;
  contact_email?: string | null;
  /** Captured at registration; used to pre-fill onboarding Step 3 research URL. */
  website_url?: string | null;
}

type OrgState = {
  orgs: Organization[];
  activeOrgId: string | null;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
};

type OrgActions = {
  fetchOrgs: () => Promise<void>;
  setActiveOrgId: (orgId: string) => void;
  reset: () => void;
};

const getStoredActive = (): string | null => {
  try {
    return localStorage.getItem(storageKey('active_org_id'));
  } catch {
    return null;
  }
};

const setStoredActive = (orgId: string | null) => {
  try {
    if (orgId) localStorage.setItem(storageKey('active_org_id'), orgId);
    else localStorage.removeItem(storageKey('active_org_id'));
  } catch {}
};

export const useOrgStore = create<OrgState & OrgActions>((set, get) => ({
  orgs: [],
  activeOrgId: getStoredActive(),
  isLoading: false,
  error: null,
  initialized: false,

  fetchOrgs: async () => {
    try {
      set({ isLoading: true, error: null });
      const res = await apiFetch('/api/orgs');
      if (!res.ok) throw new Error('Failed to fetch organizations');
      const payload = await res.json();
      const orgs: Organization[] = payload?.data || [];
      let active = get().activeOrgId;
      if (!active || !orgs.some(o => o.id === active)) {
        active = orgs[0]?.id || null;
        setStoredActive(active);
      }
      set({ orgs, activeOrgId: active, isLoading: false, initialized: true });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to fetch orgs', isLoading: false, initialized: true });
    }
  },

  setActiveOrgId: (orgId: string) => {
    setStoredActive(orgId);
    set({ activeOrgId: orgId });

    // Sync the new org's default language to i18n (only if user hasn't set a personal override)
    const org = get().orgs.find(o => o.id === orgId);
    if (org?.default_language && !getLocaleOverride()) {
      setLocale(org.default_language);
    }
  },

  reset: () => {
    setStoredActive(null);
    set({ orgs: [], activeOrgId: null, isLoading: false, error: null, initialized: false });
  },
}));

export const getActiveOrgId = (): string | null => {
  try {
    return useOrgStore.getState().activeOrgId || getStoredActive();
  } catch {
    return getStoredActive();
  }
};


